/**
 * GlitchTip error reporting for the Flexar SPA.
 *
 * Added whole by the Flexar fork — upstream RAGFlow reports errors nowhere.
 *
 * GlitchTip speaks the Sentry protocol, so the client is `@sentry/react`. Two
 * decisions are worth knowing:
 *
 * - **The DSN comes from the running container, not from the build.** Vite
 *   substitutes `import.meta.env` while bundling, so a DSN taken from there would
 *   tie one image to one installation — and every installation has its own
 *   GlitchTip. Instead the entrypoint writes `web/dist/env.js` from the
 *   environment (`SENTRY_FRONTEND_DSN` → `window.__FLEXAR_ENV__`), and
 *   `index.html` loads it before the bundle. `VITE_SENTRY_DSN` stays as the
 *   fallback for a deployment that serves the bundle without our entrypoint.
 *   Same shape as `PRESENTON_URL`, which already prefers a runtime `window`
 *   value over its build-time one.
 * - **The environment is decided at runtime, from the host.** The same image is
 *   promoted from the stand to production, so a baked-in `environment` would
 *   label stand errors as production — the exact confusion the tag exists to
 *   prevent. The production hosts are the ones nginx answers to
 *   (`flexar-devops-production/nginx/conf.d/flexar.conf` → `server_name`).
 *
 * Reporting nothing is a normal state: without a DSN the SDK is never started,
 * so `npm run dev` costs nothing and sends nothing.
 */
import * as Sentry from '@sentry/react';

/** Hosts nginx serves the production deployment on. */
const PRODUCTION_HOSTS = ['app.flexar.ai', 'flexar.friflex.com'];
const DEVELOPMENT_HOSTS = ['localhost', '127.0.0.1'];

export function resolveEnvironment(hostname: string): string {
  if (PRODUCTION_HOSTS.includes(hostname)) {
    return 'production';
  }
  if (DEVELOPMENT_HOSTS.includes(hostname)) {
    return 'development';
  }
  return 'staging';
}

/** Runtime configuration written into `env.js` by the container entrypoint. */
type RuntimeEnv = { SENTRY_DSN?: string };

/**
 * The DSN this page should report to.
 *
 * The container wins over the build: an image promoted to another installation
 * carries the bundle, not that installation's DSN.
 */
export function resolveDsn(): string | undefined {
  const runtime = (window as unknown as { __FLEXAR_ENV__?: RuntimeEnv })
    .__FLEXAR_ENV__;
  return runtime?.SENTRY_DSN || import.meta.env?.VITE_SENTRY_DSN;
}

/**
 * Start error reporting. Call once, before anything else runs — a crash during
 * bootstrap is exactly the one worth seeing.
 *
 * Returns whether the SDK was started.
 */
export function initGlitchTip(): boolean {
  // Read at call time: Jest compiles this module to CommonJS, where `import.meta`
  // is an empty object rather than Vite's env.
  const dsn = resolveDsn();
  if (!dsn) {
    return false;
  }

  Sentry.init({
    dsn,
    environment: resolveEnvironment(window.location.hostname),
    // Everything the user types here is customer data; nothing about them is
    // worth an IP address or a cookie attached to the report.
    sendDefaultPii: false,
    integrations: [
      // Console arguments are joined verbatim into the breadcrumb message, and
      // `console.error` is where failed API responses land — answers, chat text,
      // document names. The rest of the breadcrumbs (clicks, navigation, request
      // URLs and statuses) carry no payload and stay on.
      //
      // `drop_console` in the Vite build does strip all 93 of our own calls, but
      // it is an optimiser flag, not a privacy control: turn it off — or ship an
      // unminified build with a DSN — and the channel reopens with nothing
      // failing. It is also not empty today: 70 `console.error` (343 `console.*`)
      // survive in the shipped bundle, from prebuilt assets (Monaco, the docs
      // site) and six vendor references in the chunks.
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
  });
  return true;
}
